import { ChevronLeft, ChevronRight, Link2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { TimelineSummaryPanel } from "@/components/timeline/timeline-summary-panel";
import type {
  TimelineEventView,
  TimelineSummaryError,
  TimelineSummaryPreview,
  TimelineSummaryRequestState,
} from "@/features/timeline";
import type { SubscriptionTier } from "@/types/projects";

type TimelineDetailPanelProps = {
  collapsed?: boolean;
  event: TimelineEventView | null;
  onApproveSummary: () => void;
  onDismissSummaryPreview: () => void;
  onGenerateSummary: () => void;
  onOpenLink: (href: string) => void;
  onToggleCollapsed: () => void;
  summaryError: TimelineSummaryError | null;
  summaryPreview: TimelineSummaryPreview | null;
  summaryRequestState: TimelineSummaryRequestState;
  tier: SubscriptionTier;
};

export function TimelineDetailPanel({
  collapsed = false,
  event,
  onApproveSummary,
  onDismissSummaryPreview,
  onGenerateSummary,
  onOpenLink,
  onToggleCollapsed,
  summaryError,
  summaryPreview,
  summaryRequestState,
  tier,
}: TimelineDetailPanelProps) {
  const { t } = useTranslation();

  if (collapsed) {
    return (
      <aside className="flex items-start justify-center border-l border-ink/10 pl-2 pt-2 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-1">
          <button
            aria-label={t("timeline.detail.expandPanel", {
              defaultValue: "Expand event details",
            })}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="[writing-mode:vertical-rl] text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("timeline.detail.eyebrow")}
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="min-h-0 border-l border-ink/10 pl-5 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)] xl:max-h-[calc(100vh-8rem)] xl:overflow-hidden">
      <div className="flex h-full min-h-0 flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("timeline.detail.eyebrow")}
            </p>
            <h2 className="font-display text-[2rem] leading-none tracking-tight text-foreground">
              {event ? event.title : t("timeline.detail.emptyTitle")}
            </h2>
          </div>
          <Button
            onClick={onToggleCollapsed}
            size="sm"
            type="button"
            variant="outline"
          >
            <ChevronRight className="h-4 w-4" />
            {t("timeline.detail.hidePanel", {
              defaultValue: "Hide details",
            })}
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
          {!event ? (
            <div className="rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
              <p className="leading-7 text-muted-foreground">{t("timeline.detail.emptyBody")}</p>
            </div>
          ) : (
            <>
              <section className="space-y-4 rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
                <div className="space-y-2">
                  <p className="text-sm uppercase tracking-[0.18em] text-clay">{event.eraLabel}</p>
                  <p className="text-sm leading-7 text-muted-foreground">{event.description}</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-base font-semibold text-foreground">
                      {t("timeline.links.title")}
                    </h3>
                  </div>

                  {event.links.length === 0 ? (
                    <div className="rounded-[1.1rem] border border-dashed border-border/80 px-4 py-4 text-sm text-muted-foreground">
                      {t("timeline.links.empty")}
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {event.links.map((link) => (
                        <li
                          className="rounded-[1.15rem] border border-white/70 bg-white/72 px-4 py-4"
                          key={link.id}
                        >
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                {t(link.linkedKindLabelKey)}
                              </p>
                              <p className="text-base font-semibold text-foreground">{link.label}</p>
                              <p className="text-sm text-muted-foreground">{link.secondaryText}</p>
                            </div>
                            {link.broken && link.repairHintKey ? (
                              <p className="inline-flex items-center gap-2 text-sm text-destructive">
                                <TriangleAlert className="h-4 w-4" />
                                {t(link.repairHintKey)}
                              </p>
                            ) : null}
                            {link.openHref ? (
                              <Button
                                onClick={() => onOpenLink(link.openHref!)}
                                size="sm"
                                type="button"
                                variant="outline"
                              >
                                {t("timeline.links.openAction")}
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <TimelineSummaryPanel
                event={event}
                onApproveSummary={onApproveSummary}
                onDismissPreview={onDismissSummaryPreview}
                onGenerateSummary={onGenerateSummary}
                summaryError={summaryError}
                summaryPreview={summaryPreview}
                summaryRequestState={summaryRequestState}
                tier={tier}
              />
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
